import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, Optional, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { environment } from "environments/environment";
import _ from "lodash";
import { firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ItemDetail } from "@/model/entities/item-detail";
import { ItemDetailStatusEnum } from "@/model/entities/enums";
import { JoDetail } from "@/model/entities/jo-detail";
import { JoHeader } from "@/model/entities/jo-header";
import { JoStatusCode } from "@/model/enums/jo-status-code";
import { PoStatusCode } from "@/model/enums/po-status-code";
import { AddonUtilsService } from "@/shared/addon-utils.service";
import { BarcodeService } from "@/shared/barcode.service";
import { DialogBaseComponent } from "@/shared/dialog-base.component";
import { DomainBaseComponent, wrapBusy } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { UtilFns } from "@/shared/util-fns";
import { OrderFacilitationManagerComponent } from "./order-facilitation-manager.component";
import { PoCancelRequestQueueService } from "./po-cancel-request-queue.service";
import { SubstitutionConfirmDialogComponent } from "./substitution-confirm-dialog.component";

interface BackorderedLine {
  joDetail: JoDetail;
  orderQty: number;
  cancelQty: number;
  allocatedCount: number;
  backorderQty: number;
}

@Component({
  selector: "app-fill-from-inventory",
  templateUrl: "./fill-from-inventory.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FillFromInventoryComponent extends DomainBaseComponent {
  @ViewChild("joSearchInput") joSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild("itemBarcodeInput") itemBarcodeInput?: ElementRef<HTMLInputElement>;

  // Step 1: J/O search
  joNumberInput = "";
  selectedJoHeader: JoHeader | null = null;

  // Step 2: backordered lines
  backorderedLines: BackorderedLine[] = [];
  selectedBackorderedLine: BackorderedLine | null = null;

  // Step 3: item lookup
  itemBarcodeInputValue = "";
  selectedItem: ItemDetail | null = null;

  constructor(
    protected override domainService: DomainService,
    protected route: ActivatedRoute,
    private http: HttpClient,
    private addonUtilsService: AddonUtilsService,
    private barcodeService: BarcodeService,
    private queueService: PoCancelRequestQueueService,
    @Optional() private orderFacilitationManager: OrderFacilitationManagerComponent,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());
  }

  async updateFromContext() {
    this.isPageReady = true;
    await UtilFns.wait(1);
    this.joSearchInput?.nativeElement?.focus();
  }

  // ---------- Step 1: find the J/O ----------

  onJoNumberInputChange(value: string) {
    if ((value ?? "").length === 7) {
      this.findJoHeader();
    }
  }

  @wrapBusy
  async findJoHeader() {
    this.selectedJoHeader = null;
    this.backorderedLines = [];
    this.selectedBackorderedLine = null;
    this.selectedItem = null;

    const idText = (this.joNumberInput || "").trim();
    if (!idText) {
      this.toastr.error("Enter a J/O number.");
      return;
    }
    const joId = Number.parseInt(idText, 10);
    if (!Number.isFinite(joId)) {
      this.toastr.error("J/O number must be numeric.");
      return;
    }

    const q = this.uow
      .createQuery(JoHeader, "JoHeaders")
      .where({ id: joId })
      .expand([
        "account",
        "joDetails.product.productType.manufacturer",
        "joDetails.itemDetails",
        "joDetails.poDetails.poDetailCancels",
        "joDetails.poDetails.itemDetails",
        "joDetails.poDetails.poHeader.manufacturer",
        "joDetails.joDetailAddons.addon",
        "joDetails.cancellationRequestDetails",
      ]);
    const r = await q.execute();
    const joh = _.first(r);
    if (!joh) {
      this.toastr.error(`J/O ${joId} not found.`);
      return;
    }

    // Eligibility: J/O must be Open/Process Complete. Incomplete J/Os haven't
    // finished their pull/backorder step yet and don't participate in
    // facilitation; Closed JOs have no work left to do.
    if (joh.joStatusId !== JoStatusCode.OpenProcessComplete) {
      const statusLabel =
        joh.joStatusId === JoStatusCode.OpenProcessIncomplete
          ? "still in the pull/backorder step (Open/Process Incomplete)"
          : joh.joStatusId === JoStatusCode.Closed
          ? "closed"
          : `in status ${joh.joStatusId}`;
      this.toastr.error(
        `J/O ${joId} is ${statusLabel}; only Open/Process Complete J/Os are eligible for facilitation.`,
      );
      return;
    }

    this.selectedJoHeader = joh;
    this.backorderedLines = this.computeBackorderedLines(joh);
    if (this.backorderedLines.length === 0) {
      this.toastr.error(`J/O ${joId} has no backordered lines to fill.`);
    }
  }

  private computeBackorderedLines(joh: JoHeader): BackorderedLine[] {
    return (joh.joDetails ?? [])
      .map((jod) => {
        const orderQty = jod.orderQty ?? 0;
        const cancelQty = jod.cancelQty ?? 0;
        const allocatedCount = (jod.itemDetails ?? []).length;
        const backorderQty = orderQty - cancelQty - allocatedCount;
        return { joDetail: jod, orderQty, cancelQty, allocatedCount, backorderQty };
      })
      .filter((r) => r.backorderQty > 0);
  }

  selectBackorderedLine(line: BackorderedLine) {
    this.selectedBackorderedLine = line;
    this.selectedItem = null;
    this.itemBarcodeInputValue = "";
    setTimeout(() => this.itemBarcodeInput?.nativeElement?.focus(), 0);
  }

  // ---------- Step 3: find the in-inventory item ----------

  onItemBarcodeInputChange(value: string) {
    if ((value ?? "").length === 12) {
      this.findItem();
    }
  }

  @wrapBusy
  async findItem() {
    this.selectedItem = null;

    const barcode = (this.itemBarcodeInputValue || "").trim();
    if (!barcode) {
      this.toastr.error("Enter or scan an item barcode.");
      return;
    }

    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ id: barcode })
      .expand([
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice.feature",
        "itemDetailAddons.addon",
      ]);
    const r = await q.execute();
    const item = _.first(r);
    if (!item) {
      this.toastr.error(`Item ${barcode} not found.`);
      return;
    }
    if (item.joDetailId != null) {
      this.toastr.error("This item is already allocated to another J/O.");
      return;
    }
    if (item.itemDetailStatusId !== ItemDetailStatusEnum.InInventory) {
      this.toastr.error("This item is not currently in inventory.");
      return;
    }

    // F-1 client-side pre-check (server enforces authoritatively).
    const jod = this.selectedBackorderedLine!.joDetail;
    if (item.accountId != null && item.accountId !== jod.joHeader.accountId) {
      this.toastr.error("Unable to apply an inventory item owned by another account.");
      return;
    }
    // F-2 client-side pre-check (server enforces authoritatively).
    if (item.accountId != null && !jod.joHeader.allowInventory) {
      this.toastr.error(
        "Unable to apply an account-owned inventory item — this J/O is not permitted to draw from account inventory.",
      );
      return;
    }

    this.selectedItem = item;
  }

  /**
   * Mirror of the server's P/O Detail selection in FillFromInventory:
   * first P/O Detail with open qty, skipping special-manufacturer P/O Details,
   * ordered by pd.Id.
   */
  private pickTargetPoDetail(jod: JoDetail) {
    const pods = jod.poDetails ?? [];
    const candidates = pods
      .filter((pd) => pd.manufacturerProductTypeConfigId == null)
      .map((pd) => {
        const cancelled = _.sumBy(pd.poDetailCancels ?? [], (c) => c.qty ?? 0);
        const received = (pd.itemDetails ?? []).length;
        const openQty = (pd.orderQty ?? 0) - cancelled - received;
        return { pd, openQty };
      })
      .filter((x) => x.openQty > 0);
    const sorted = _.sortBy(candidates, (x) => x.pd.id);
    return _.first(sorted)?.pd;
  }

  isSubstitution(): boolean {
    return (
      !!this.selectedItem &&
      !!this.selectedBackorderedLine &&
      this.selectedItem.productId !== this.selectedBackorderedLine.joDetail.productId
    );
  }

  canApply(): boolean {
    return !!this.selectedJoHeader && !!this.selectedBackorderedLine && !!this.selectedItem;
  }

  // ---------- Apply ----------

  @wrapBusy
  async apply() {
    if (!this.canApply()) return;
    const item = this.selectedItem!;
    const line = this.selectedBackorderedLine!;
    const jod = line.joDetail;

    // Substitution confirmation happens client-side only. The server trusts
    // the request per design (docs/in-flight-order-changes.md §3.1).
    if (this.isSubstitution()) {
      const ok = await DialogBaseComponent.open(SubstitutionConfirmDialogComponent, this, {
        expectedProduct: jod.product,
        actualProduct: item.product,
      });
      if (!ok) return;
    }

    // Pre-check the target P/O Detail. Only relevant for POSTED P/Os: if the
    // manufacturer has no WhEmail on file, warn the operator before proceeding.
    // Unposted P/Os never involve a manufacturer email (server adjusts the
    // P/O Detail in place) so no pre-check is needed.
    const targetPod = this.pickTargetPoDetail(jod);
    const isPrePost = targetPod?.poHeader?.poStatusId === PoStatusCode.NewUnposted;
    if (targetPod && !isPrePost) {
      const manufacturer = targetPod.poHeader?.manufacturer;
      const hasEmail = !!manufacturer?.whEmail && manufacturer.whEmail.trim().length > 0;
      if (!hasEmail) {
        const mfrName = manufacturer?.name ?? "the manufacturer";
        const ok = await this.dialogService.yesNoDialog(
          "Manufacturer email not on file",
          `${mfrName} has no cancellation-request email on file. If you proceed, ` +
            `the cancellation on P/O ${targetPod.poHeaderId} will still be recorded in ` +
            `Uniformax, but no email will be sent to the manufacturer. Proceed?`,
        );
        if (!ok) return;
      }
    }

    const actor = this.authUser?.initials ?? "";
    const url = `${environment.apiRoot}breeze/uniformax/FillFromInventory`;
    let affectedPoDetailId: number | null = null;
    let emailQueued = false;
    try {
      const res = await firstValueFrom(
        this.http.post<{ AffectedPoDetailId: number | null; EmailQueued: boolean }>(url, {
          ItemDetailId: item.id,
          RecipientJoDetailId: jod.id,
          Actor: actor,
        }),
      );
      affectedPoDetailId = res?.AffectedPoDetailId ?? null;
      emailQueued = !!res?.EmailQueued;
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.message ?? "Unknown server error.";
      this.toastr.error(msg, "Could not fill from inventory");
      return;
    }

    // NOTE: the manufacturer cancel-request email is no longer fired inline.
    // The server enqueues a PoCancelRequestQueue row (post-post fills only);
    // the SendPoCancelRequestBatch Hangfire job (every 15 min) or the operator's
    // Flush Now action on the Pending Cancellations tray sends it in a batch
    // aggregated by manufacturer. See design doc §4.5.1.

    // The server changed the entity state in the DB. Clear + reload the item
    // and J/O Detail so client-side reflects reality, then use AddonUtilsService
    // to create the fresh ItemDetailAddon rows.
    this.uow.clearEntities(ItemDetail);
    this.uow.clearEntities(JoDetail);

    const refreshedItem = _.first(
      await this.uow
        .createQuery(ItemDetail, "ItemDetails")
        .where({ id: item.id })
        .expand(["product", "itemDetailAddons.addon"])
        .execute(),
    );
    const refreshedJod = _.first(
      await this.uow
        .createQuery(JoDetail, "JoDetails")
        .where({ id: jod.id })
        .expand([
          "joHeader",
          "product.productFeatureChoices.featureChoice.feature",
          "joDetailAddons.addon",
        ])
        .execute(),
    );
    if (refreshedItem && refreshedJod) {
      const accountAddons = await this.dbQueryService.getAddons(refreshedJod.joHeader.accountId);
      this.addonUtilsService.createItemDetailAddonsFromJoDetail(refreshedJod, refreshedItem, accountAddons ?? []);
      const addonBins = await this.addonUtilsService.findOrCreateAddonBins([refreshedItem]);
      await this.addonUtilsService.checkAndRemoveEmptyBins(addonBins.map((x) => x.id));

      if (refreshedItem.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon) {
        const unassigned = refreshedItem.itemDetailAddons.filter((ida) => ida.addonBinId == null);
        if (unassigned.length > 0) {
          throw new Error(
            `ItemDetail ${refreshedItem.id}: ${unassigned.length} of ${refreshedItem.itemDetailAddons.length} addon(s) missing addonBinId after findOrCreateAddonBins; aborting save.`,
          );
        }
      }

      await this.dbSaveService.saveChanges();

      // Print ItemDetail barcode + any new AddonBin labels, same as the
      // receive-from-manufacturer path (barcode.service.ts). Op A is
      // treating this inventory item as if the cancelled expected item
      // was going through the receive step.
      const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels([refreshedItem]);
      if (changes.length > 0) {
        await this.dbSaveService.saveSelectedChanges(changes);
      }

      // Safety net for printer jams / offline printer. Loop reprints just the
      // ItemDetail barcode (bin labels are one-time and already Open). Uses
      // yesNoDialog (self-contained) rather than askYesNo because the latter
      // reuses PrimeNG's ConfirmationService Subject across invocations and
      // throws "object unsubscribed" on the second close.
      while (!(await this.dialogService.yesNoDialog("Printed OK?", "Did the barcode print correctly?"))) {
        await this.barcodeService.printItemDetailBarcodes([refreshedItem]);
      }
    }

    // Outcome-specific toast so the operator isn't guessing why the tray
    // did or didn't change. Three cases:
    //   1. Post-post fill → queued for manufacturer.
    //   2. Pre-post fill  → P/O Detail adjusted in place, no email needed.
    //   3. No matching P/O Detail with open qty → nothing to cancel.
    const baseMsg = `Item ${item.id} applied to J/O ${jod.joHeaderId}.`;
    if (affectedPoDetailId != null && emailQueued) {
      const mfrName = targetPod?.poHeader?.manufacturer?.name ?? "manufacturer";
      this.toastr.success(`${baseMsg} Cancel-request queued for ${mfrName}.`);
    } else if (affectedPoDetailId != null && !emailQueued) {
      this.toastr.success(`${baseMsg} P/O not yet posted — P/O Detail adjusted, no email needed.`);
    } else {
      this.toastr.success(`${baseMsg} No open P/O qty to cancel.`);
    }
    this.queueService.refreshRequested$.next();
    await this.refreshAfterApply();
  }

  private async refreshAfterApply() {
    const joh = this.selectedJoHeader;
    if (!joh) {
      this.reset();
      return;
    }

    this.uow.clearEntities(JoHeader);
    const refreshed = _.first(
      await this.uow
        .createQuery(JoHeader, "JoHeaders")
        .where({ id: joh.id })
        .expand([
          "account",
          "joDetails.product.productType.manufacturer",
          "joDetails.itemDetails",
          "joDetails.poDetails.poDetailCancels",
          "joDetails.poDetails.itemDetails",
          "joDetails.poDetails.poHeader.manufacturer",
          "joDetails.joDetailAddons.addon",
        ])
        .execute(),
    );

    this.selectedBackorderedLine = null;
    this.selectedItem = null;
    this.itemBarcodeInputValue = "";

    if (!refreshed) {
      this.reset();
      return;
    }
    this.selectedJoHeader = refreshed;
    this.backorderedLines = this.computeBackorderedLines(refreshed);

    if (this.backorderedLines.length === 0) {
      this.toastr.success(`J/O ${refreshed.id} has no more backordered lines.`);
      this.reset();
      return;
    }
  }

  reset() {
    this.joNumberInput = "";
    this.selectedJoHeader = null;
    this.backorderedLines = [];
    this.selectedBackorderedLine = null;
    this.itemBarcodeInputValue = "";
    this.selectedItem = null;
    setTimeout(() => this.joSearchInput?.nativeElement?.focus(), 0);
  }
}
